// Rewrite önizleme — spec §7.2. qcStatus="ready" + promptType="rewrite"'ta orta
// panelde editörün yerini alır. İki sekme: "Yan Yana" (eski + yeni aynı anda) ve
// "Birleşik Diff" (mevcut DiffView reuse). Kabul → onAccept, Reddet → opsiyonel
// sebep modalı → onReject. Kararı parent (Review.tsx) accept/rejectQc + loadSections'a bağlar.
import { useState } from "react";
import type { ReviewSection } from "@mahir/shared";
import { DiffView } from "./DiffView";
import { SideBySideDiff } from "./SideBySideDiff";
import { REVIEW_COPY } from "../../lib/review-copy";

interface QcPreviewProps {
  section: ReviewSection;
  busy: boolean;
  onAccept: () => void;
  onReject: (reason?: string) => void;
  /** Final review I2: KK sürerken editöre yazılmışsa Kabul pasif — kabul edilen metin yazılanla
   *  sessizce ayrışırdı (Kaydet adayı ezerdi). */
  hasUnsavedChanges?: boolean;
  onDiscardLocal?: () => void;
}

export function QcPreview({
  section,
  busy,
  onAccept,
  onReject,
  hasUnsavedChanges = false,
  onDiscardLocal,
}: QcPreviewProps): JSX.Element {
  const [tab, setTab] = useState<"side" | "diff">("side");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const meta = section.qcMeta;
  const lowCoverage = meta?.coverageRatio != null && meta.coverageRatio < 0.7;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {(["side", "diff"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="px-3 py-1 rounded-t text-xs"
              style={{
                background: tab === t ? "var(--color-surface)" : "transparent",
                color: tab === t ? "var(--color-text)" : "var(--color-text-3)",
                fontWeight: 600,
              }}
            >
              {t === "side" ? "Yan Yana" : "Birleşik Diff"}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {hasUnsavedChanges && (
            <span
              className="self-center text-xs"
              data-testid="qc-accept-unsaved"
              style={{ color: "var(--color-warning-text)" }}
            >
              {REVIEW_COPY.unsavedBlocksAction}
              {onDiscardLocal && (
                <>
                  {" · "}
                  <button type="button" className="underline" onClick={onDiscardLocal}>
                    {REVIEW_COPY.discardLocal}
                  </button>
                </>
              )}
            </span>
          )}
          <button
            onClick={onAccept}
            disabled={busy || hasUnsavedChanges}
            title={hasUnsavedChanges ? REVIEW_COPY.unsavedBlocksAction : undefined}
            className="px-3 py-1.5 rounded-md text-sm disabled:opacity-50"
            style={{ background: "var(--color-success)", color: "white", fontWeight: 600 }}
          >
            ✔ Kabul Et
          </button>
          <button
            onClick={() => setRejectOpen(true)}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-sm disabled:opacity-50"
            style={{ border: "1px solid var(--color-error)", color: "var(--color-error)" }}
          >
            ✖ Reddet…
          </button>
        </div>
      </div>
      {meta && (
        <p className="text-xs mb-2" style={{ color: "var(--color-text-3)" }}>
          {meta.promptName} · {meta.model} · {meta.tokensTotal} token · ${meta.costUsd.toFixed(4)}
        </p>
      )}
      {lowCoverage && (
        <div
          className="mb-3 rounded-md p-3 text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-warning) 10%, white)",
            border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
            color: "var(--color-text)",
          }}
        >
          <strong style={{ color: "var(--color-warning)" }}>⚠ Eksik yansıma uyarısı</strong> —
          adayda form yanıtlarındaki somut bilgilerin yalnızca %
          {Math.round((meta.coverageRatio ?? 0) * 100)}&apos;i tespit edildi.
        </div>
      )}
      {tab === "side" ? (
        <SideBySideDiff original={section.content ?? ""} candidate={section.qcCandidate ?? ""} />
      ) : (
        <DiffView original={section.content ?? ""} current={section.qcCandidate ?? ""} />
      )}
      {rejectOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ background: "rgba(0,0,0,0.4)" }}
          onClick={() => setRejectOpen(false)}
        >
          <div
            className="rounded-xl p-6 max-w-md w-full mx-4"
            style={{ background: "var(--color-surface)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2" style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem" }}>
              Adayı Reddet
            </h3>
            <p className="text-xs mb-3" style={{ color: "var(--color-text-2)" }}>
              Neden uygun değildi? (opsiyonel — yöneticinin promptu iyileştirmesine yardım eder)
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              className="w-full p-2 rounded border text-sm"
              style={{ borderColor: "var(--color-border)", minHeight: 80 }}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => {
                  setRejectOpen(false);
                  setReason("");
                }}
                className="px-3 py-1.5 rounded-md text-sm"
                style={{ border: "1px solid var(--color-border)" }}
              >
                Vazgeç
              </button>
              <button
                onClick={() => {
                  onReject(reason.trim() || undefined);
                  setRejectOpen(false);
                  setReason("");
                }}
                disabled={busy}
                className="px-3 py-1.5 rounded-md text-sm disabled:opacity-50"
                style={{ background: "var(--color-error)", color: "white", fontWeight: 600 }}
              >
                Reddet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
